import { WorkspacesState } from 'common/store/workspaces/reducer';
import { WorkspaceGroup, WorkspaceItem } from 'common/store/common/workspaceScheme/types';
import { UserPrivileges } from 'common/store/session/constants';
import commonWorkspaceSchemeService from 'common/services/commonWorkspaceSchemeService';
import { WorkflowKey, WorkspaceSchemeModel } from 'common/store/workspaces/types';
import { workspaceID } from 'common/store/common/constants';
import { Committee, getCommitteeById, getUserRolesInCommittee } from './committeeService';
import storeManager from '../store';
import { RootState } from '../store/rootReducer';
import { CommitteeType } from '../store/assessment/constants';
import { getPartyCommitteeIdForContext } from './gradingPartyService';
import { isMultipleAttemptsParentTest } from './assessmentService';

export const getNumberOfVisibleWorkspaces = (workspaces: WorkspacesState, selectedCommitteeId: number): number => {
    const { assessment } = storeManager.getState() as RootState;
    let availableWorkspaceGroups = getVisibleWorkspaces(workspaces);
    if (assessment?.committees) {
        availableWorkspaceGroups = filterAvailableGroupsForUserInCommittee(availableWorkspaceGroups, selectedCommitteeId);
    }
    let acc = 0;

    if (availableWorkspaceGroups.length > 0) {
        availableWorkspaceGroups.forEach((group: WorkspaceGroup) => {
            acc += group.workspaces.length;
        });
    }

    return acc;
};

/*
    if current test has committees, we need to check permission for grader :
     1. if no committee selected, then hide
     2. if has committee selected, but user is not in current committee, then hide
*/
export const filterAvailableGroupsForUserInCommittee = (workspaceGroups: WorkspaceGroup[], selectedCommitteeId: number) => {
    const currentCommittee = getCommitteeById(selectedCommitteeId);
    const userRolesInCommittee = getUserRolesInCommittee(currentCommittee);
    return workspaceGroups.filter((group: WorkspaceGroup) => {
        return !(group.type === UserPrivileges.ar_evaluate
                && (selectedCommitteeId === -1
                || !userRolesInCommittee.includes(group.type)));
    });
};

export const getVisibleWorkspaces = (workspaces: WorkspacesState): WorkspaceGroup[] => {
    const { context } = storeManager.getState() as RootState;
    const selectedCommitteeId = getPartyCommitteeIdForContext(context.partyState.gradingPartyDocument);
    const selectedCommittee: Committee = getCommitteeById(selectedCommitteeId);

    let userAvailableWorkspaces = commonWorkspaceSchemeService.getAvailableWorkspacesForUser(workspaces);

    const filterWorkspaces = (data: WorkspaceGroup[]) => {
        if (isMultipleAttemptsParentTest()) {
            const excludeWorkspaceGroupList = [workspaceID.EVALUATE_OVERVIEW, workspaceID.EVALUATE_MARKING,
                workspaceID.PLAN_OVERVIEW, workspaceID.PLAN_MARKING];

            return data.map((group) => ({
                ...group,
                workspaces: group.workspaces.filter((workspace) => {
                    if ('internal_ref' in workspace) {
                        return !excludeWorkspaceGroupList.some((excludeId) => workspace.internal_ref.includes(excludeId));
                    }
                    return true;
                }),
            })).filter((group) => group.workspaces.length > 0);
        }
        return data;
    };
    userAvailableWorkspaces = filterWorkspaces(userAvailableWorkspaces);
    const filteredWorkspaceGroups: WorkspaceGroup[] = [];

    if (selectedCommittee?.type === CommitteeType.reassessment) {
        userAvailableWorkspaces.forEach((workspaceGroup: WorkspaceGroup) => {
            const wSpaces: WorkspaceItem[] = [];
            workspaceGroup.workspaces.forEach((workspace: WorkspaceItem) => {
                const workspaceId = commonWorkspaceSchemeService.getWorkspaceIdentifier(workspace);

                if (!workspaceId.includes('explanations')
                    && !workspaceId.includes('student_report')) {
                    wSpaces.push(workspace);
                }
            });

            if (wSpaces.length > 0) {
                filteredWorkspaceGroups.push({
                    workspaces: wSpaces,
                    type: workspaceGroup.type,
                });
            }
        });

        return filteredWorkspaceGroups;
    }

    return userAvailableWorkspaces;
};

export const getWorkspaceIdByWorkflowKey = (workspaceScheme: WorkspaceSchemeModel, workflowKeyValue: string): string => {
    const workflowKey = workspaceScheme?.workflowKeys?.find(
        (workflowKey: WorkflowKey) => workflowKey.key === workflowKeyValue);

    return workflowKey?.id;
};

export const getStudentWorkspaceVisibleFromTime = (): string => {
    const { workspaces } = storeManager.getState() as RootState;
    const { workspaceScheme: { workspaceGroups } } = workspaces;
    const studentWorkspaceGroup: WorkspaceGroup = workspaceGroups?.find((workspaceGroup: WorkspaceGroup) => workspaceGroup.type === 'student');

    if (studentWorkspaceGroup) {
        const studentReportWorkspace: WorkspaceItem = studentWorkspaceGroup.workspaces?.find((workspaceItem: WorkspaceItem) => {
            if ('internal_ref' in workspaceItem) {
                if (workspaceItem.internal_ref === 'student_report') {
                    return true;
                }
            }

            return false;
        });

        return studentReportWorkspace?.visibleFrom;
    }

    return null;
};
